if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('.sticky-atc__button');
        this.priceEl = this.querySelector('.sticky-atc__price');
        this.variantTitleEl = this.querySelector('.sticky-atc__variant-title');
        this.sectionId = this.dataset.sectionId;
        this.targetButton = document.getElementById(this.dataset.targetButton);
        this.visibilityObserver = null;
        this.stateObserver = null;
        this.unsubscribeVariantChange = null;
      }

      connectedCallback() {
        // If the real Add to cart button isn't there for some reason, stay hidden
        // rather than show a bar that can't actually add anything to the cart.
        if (!this.targetButton || !this.button) return;

        this.setupClickProxy();
        this.setupVisibilityObserver();
        this.setupStateSync();
        this.setupVariantChangeListener();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.stateObserver?.disconnect();
        this.unsubscribeVariantChange?.();
      }

      // Tapping the sticky button simply clicks the real submit button, so the
      // exact same variant id, quantity, and cart/error handling Dawn already
      // uses is what runs -- nothing about "which variant" is duplicated here.
      setupClickProxy() {
        this.button.addEventListener('click', () => {
          if (this.button.disabled) return;
          if (this.targetButton.disabled || this.targetButton.getAttribute('aria-disabled') === 'true') return;
          this.targetButton.click();
        });
      }

      // Only show the bar once the visitor has scrolled past the real
      // Add to cart button, and hide it again if they scroll back up to it,
      // so it never sits on top of content that's already visible.
      setupVisibilityObserver() {
        if (!('IntersectionObserver' in window)) {
          this.classList.add('sticky-atc--visible');
          return;
        }

        const anchor = this.targetButton.closest('.product-form__buttons') || this.targetButton;

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle('sticky-atc--visible', scrolledPast);
            });
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(anchor);
      }

      // Mirror the real button's disabled/text state (which Dawn already keeps
      // correct for the selected size/colour) onto the sticky button, instead
      // of recomputing variant availability ourselves.
      setupStateSync() {
        const syncState = () => {
          this.button.disabled = this.targetButton.hasAttribute('disabled');
          const targetText = this.targetButton.querySelector('span')?.textContent?.trim();
          const stickySpan = this.button.querySelector('span');
          if (targetText && stickySpan) stickySpan.textContent = targetText;
        };

        syncState();
        this.stateObserver = new MutationObserver(syncState);
        this.stateObserver.observe(this.targetButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      // Dawn publishes this event (see product-info.js) whenever the shopper
      // changes size/colour. Use the server-rendered price markup it hands us
      // so formatting/currency always matches the rest of the page exactly.
      setupVariantChangeListener() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          const variant = event.data.variant;
          if (!variant) return;

          if (this.variantTitleEl) this.variantTitleEl.textContent = variant.title;

          if (this.priceEl) {
            const priceRoot = event.data.html
              ?.getElementById(`price-${this.sectionId}`)
              ?.querySelector('.price');
            if (priceRoot) {
              const onSale = priceRoot.classList.contains('price--on-sale');
              const selector = onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
              const priceText = priceRoot.querySelector(selector)?.textContent?.trim();
              if (priceText) this.priceEl.textContent = priceText;
            }
          }
        });
      }
    }
  );
}